// Колонка списка — левая часть области раздела. Правило 2: над списком подпись, что
// перечислено и сколько («281 человек»), и пометка среза данных — данные прошлой сессии
// показываются мгновенно, но с честным временем (правило 11). Переключатель представлений
// и фильтры — слоты: их содержимое у каждого раздела своё.
//
// ## Ширина зависит от того, что в строке
//
// До 25.08.2026 колонка была 384 точки во всех разделах без исключения. В разделах с
// числами этого хватало, а в «Людях» — нет: строка держит фамилию, имя и отчество, объект,
// город, метку долга и состояние вахты. Всё это не помещалось, и обрезалась именно фамилия,
// потому что метки стоят справа и не сжимаются: на живом экране было «Багровский С…» с двумя
// метками рядом. То есть колонка приносила в жертву ровно то, ради чего человек в неё
// смотрит.
//
// Поэтому ширина объявляется разделом: `имена` для списков людей и объектов, `числа` —
// для остального. Два значения, а не свободное число: свободное разъедется по разделам за
// неделю, и мы вернёмся туда же.
//
// ## Ритм шапки
//
// Между подписью, переключателем и отборами — 12 точек, между самими отборами — тоже 12.
// Раньше везде стояло 8, и три разные по смыслу группы читались как один длинный список
// полей. Отборы обёрнуты ЗДЕСЬ, а не в разделах: пока каждый раздел клал свою обёртку,
// зазор между отборами был свой в каждом.
//
// ## Действие над выделенным живёт ВНИЗУ, а не в шапке
//
// Панель «выделено 2 человека, сменить статус пачкой» стояла среди отборов, то есть НАД
// списком. Замер 25.08.2026: два выделенных флажка добавляли в шапку 260 точек, и весь
// список уезжал вниз почти на треть экрана. Человек отмечает людей подряд, сверяясь со
// строками, — и после каждой второй галочки строки прыгают под курсором: следующее нажатие
// попадает не в того. На реестре в 286 человек это не мелочь, а неверно изменённый статус.
//
// Поэтому у колонки есть подвал: он появляется снизу, ничего не сдвигает и остаётся на месте,
// пока список прокручивают.
//
// ## Действие списка живёт ВВЕРХУ, а не в конце
//
// «Новый сотрудник» стоял кнопкой после последней строки. При 133 людях на вахте до неё было
// восемь с половиной тысяч точек прокрутки (замер 01.10.2026 на стенде): действие, которое надо
// найти за секунду, оказывалось ниже всего списка. Теперь у колонки есть слот `action`: он
// стоит в одной строке с «Отборами», справа, то есть над первой строкой и виден без прокрутки
// при любой длине списка. Это та же раскладка, что у шапки полноширинного представления
// (`shared/ui/toolbar`): отборы слева, действия справа.

import type { ReactNode } from 'react';
import { useState } from 'react';

import { RiArrowDownSLine, RiEqualizerLine } from '@remixicon/react';

import { formatDataAsOf } from '@/shared/time/format';
import { cn } from '@/utils/cn';

import { useКлавиатураСписка } from './клавиатура-списка';

export interface ListPaneProps {
  /** Что и сколько: «281 человек», «74 заявки». */
  caption: string;
  /** Момент среза данных с сервера; null — данных ещё не было, пометка не показывается. */
  sliceAt?: Date | null;
  /** Переключатель представлений раздела. */
  viewSwitcher?: ReactNode;
  /**
   * Поиск. Стоит ВСЕГДА на виду, отдельно от остальных отборов.
   *
   * Он не такой же отбор, как прочие: остальными сужают список, а поиском ищут КОНКРЕТНОГО
   * человека, и делают это чаще, чем всё остальное вместе. Спрятать его под «Отборы» значит
   * добавить нажатие к самому частому действию колонки.
   */
  search?: ReactNode;
  /**
   * То, что НЕ отбор, и потому не прячется вместе с отборами.
   *
   * Два случая, и оба про смысл списка, а не про его сужение. У постов это подпись «Объект:
   * АвтоБаза Буровая» — без неё список не отвечает, чьи это посты. На пульте подотчёта это
   * порядок, «кто дольше» против «у кого больше»: он меняет не состав списка, а сам вопрос, на
   * который список отвечает, и выбирают его раньше, чем сужают выборку. Плюс к нему привязана
   * подсказка первого раза — спрятанный якорь сжёг бы её единственный показ впустую.
   */
  note?: ReactNode;
  /** Слот фильтров: элементы перечисляются подряд, ритм между ними задаёт колонка. */
  filters?: ReactNode;
  /**
   * Названия НАЛОЖЕННЫХ отборов — «статус», «регион». Пусто — отборов нет.
   *
   * Нужны, потому что свёрнутые отборы прячут состояние списка: человек видит девять фамилий из
   * двухсот восьмидесяти шести и не понимает, это весь реестр или срез. Счётчика мало —
   * «2 отбора» не говорит, каких именно, и снимать их приходится вслепую.
   */
  appliedFilters?: readonly string[];
  /** Что в строке: `имена` — длинные имена и адреса, `числа` — суммы и метки. */
  width?: 'числа' | 'имена';
  /** Подвал: действие над выделенным. Появляется снизу и НЕ двигает список. */
  footer?: ReactNode;
  /**
   * Главное действие списка («Новый сотрудник»): справа в строке «Отборов», над первой строкой.
   * Одно. Вторая такая же кнопка в конце списка или в соседней колонке означала бы два входа в
   * одно действие, и человек гадал бы, чем они различаются.
   */
  action?: ReactNode;
  /**
   * Пояснение под строкой действия: почему оно не сработало («заводит кадровая служба»). Живёт
   * здесь, а не внутри кнопки: кнопка узкая, а причина длиннее неё.
   */
  actionNote?: ReactNode;
  children: ReactNode;
}

export function ListPane({
  caption,
  sliceAt,
  viewSwitcher,
  search,
  note,
  filters,
  appliedFilters = [],
  width = 'числа',
  footer,
  action,
  actionNote,
  children,
}: ListPaneProps) {
  // Свёрнуто по умолчанию, и это главное в правке.
  //
  // Замер 03.09.2026: в «Людях» шесть групп отборов подряд занимали 390 точек до первой фамилии
  // — из 286 человек на экране помещалось девять. Отборами при этом пользуются несколько раз в
  // день, а список читают весь день. Постоянно развёрнутый отбор — это треть колонки, отданная
  // тому, чем не пользуются.
  //
  // Состояние живёт в самой колонке, а не в адресе: адрес хранит НАЛОЖЕННЫЕ отборы, и открытость
  // ящика к ним отношения не имеет. Ссылка, присланная коллеге, не должна открывать ему ящик.
  const [отборыОткрыты, раскрытьОтборы] = useState(false);
  // Стрелки и Enter по строкам. Обработчик на ВСЕЙ колонке, а не на списке: ход начинается
  // из поля поиска, и событие всплывает из него (`клавиатура-списка.ts`).
  const клавиатура = useКлавиатураСписка();
  const наложено = appliedFilters.length;

  return (
    <section
      ref={клавиатура.колонка}
      onKeyDown={клавиатура.onKeyDown}
      className={cn(
        'flex h-full shrink-0 flex-col border-r border-stroke-soft-200 bg-bg-white-0',
        width === 'имена' ? 'w-list-names' : 'w-list',
        // В узком окне (портрет планшета — 744 точки) трёх колонок не бывает: полоса разделов
        // и список съедают их целиком, содержимому остаётся 88 точек. Поэтому одна поверхность
        // за раз: пока строка не выбрана — весь экран занимает список, и это не ухудшение, а
        // улучшение — фамилии видно лучше, чем в 416 точках на компьютере.
        'узко:w-full узко:shrink узко:border-r-0',
        'узко-строка:hidden',
      )}
    >
      <header className='flex flex-col gap-3 border-b border-stroke-soft-200 px-4 py-3'>
        <div className='flex items-baseline justify-between gap-2'>
          <h1 className='truncate text-label-md text-text-strong-950'>{caption}</h1>
          {sliceAt !== null && sliceAt !== undefined && (
            <span className='shrink-0 text-paragraph-xs text-text-sub-600'>
              {formatDataAsOf(sliceAt)}
            </span>
          )}
        </div>
        {viewSwitcher}
        {note}
        {search}
        {(filters !== undefined || action !== undefined) && (
          <div className='flex items-center justify-between gap-2'>
            {filters !== undefined && (
              <button
                type='button'
                onClick={() => раскрытьОтборы(!отборыОткрыты)}
                aria-expanded={отборыОткрыты}
                className={cn(
                  'цель-тача relative flex min-w-0 items-center gap-2 rounded-md py-1 text-left text-paragraph-sm text-text-sub-600 transition duration-200 ease-out hover:text-text-strong-950',
                  // Без действия строка целиком кнопка, и стрелка у правого края, как было. С
                  // действием кнопка сжимается до своего содержимого, стрелка стоит у слова, а
                  // место справа отдано действию.
                  action === undefined && 'flex-1',
                )}
              >
                <RiEqualizerLine className='size-4 shrink-0' />
                <span className='truncate'>
                  {наложено === 0 ? 'Отборы' : `Отбор: ${appliedFilters.join(', ')}`}
                </span>
                <RiArrowDownSLine
                  className={cn(
                    'size-4 shrink-0 transition-transform duration-200 ease-out',
                    action === undefined && 'ml-auto',
                    отборыОткрыты && 'rotate-180',
                  )}
                />
              </button>
            )}
            {action !== undefined && <div className='ml-auto shrink-0'>{action}</div>}
          </div>
        )}
        {actionNote}
        {filters !== undefined && отборыОткрыты && (
          <div className='flex flex-col gap-3'>{filters}</div>
        )}
      </header>
      <div className='min-h-0 flex-1 overflow-y-auto'>{children}</div>
      {footer !== undefined && (
        // В подвале живут действия над выделенными строками, и это про деньги и статусы:
        // отдать их полосе жеста «домой» нельзя.
        <div className='shrink-0 border-t border-stroke-soft-200 bg-bg-white-0 тач:безопасно-снизу'>
          {footer}
        </div>
      )}
    </section>
  );
}
